/**
 * Tokyo / Shanghai read-back loop shared by guaranteed-service writes and
 * the 保障配置 drawer (#3135 review B2). Callers pass the GET they poll.
 */
import type { GuaranteedPropagationState } from '@nadoutong/contracts';
import type { CnConfirmation } from './writeStatus.ts';

export interface PropagationRead {
  readonly cn: { readonly state: GuaranteedPropagationState };
}

export type PropagationReader = (
  id: string,
  expectedUpdatedAt: string | null,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
) => Promise<PropagationRead>;

interface PollTiming {
  readonly intervalMs: number;
  readonly deadlineMs: number;
}

const DEFAULT_POLL: PollTiming = { intervalMs: 2_000, deadlineMs: 60_000 };
let pollTiming: PollTiming = DEFAULT_POLL;

/** Test seam: shrink the 2 s / 60 s propagation poll. `null` restores it. */
export function setPropagationPollForTest(timing: PollTiming | null): void {
  pollTiming = timing ?? DEFAULT_POLL;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      clearTimeout(timer);
      resolve();
    }, { once: true });
  });
}

/**
 * Polls ONE id and applies its verdict to every id in `ids`.
 * A missing or failing GET is `unreachable` and is retried until the
 * 60 s deadline, then the footer names the timeout — never "received".
 */
export function watchShanghai(input: {
  readonly ids: readonly string[];
  readonly expectedUpdatedAt: string | null;
  readonly goal: 'match' | 'missing';
  readonly read: PropagationReader;
  readonly fetchImpl: typeof fetch;
  readonly pollers: Map<string, AbortController>;
  readonly mounted: { current: boolean };
  readonly setCn: (ids: readonly string[], cn: CnConfirmation) => void;
}): void {
  const pollId = input.ids[0];
  if (pollId === undefined || !input.mounted.current) return;
  for (const id of input.ids) input.pollers.get(id)?.abort();
  const controller = new AbortController();
  for (const id of input.ids) input.pollers.set(id, controller);
  const { signal } = controller;
  const startedAt = Date.now();
  void (async () => {
    for (;;) {
      let state: GuaranteedPropagationState;
      try {
        state = (await input.read(pollId, input.expectedUpdatedAt, input.fetchImpl, signal)).cn.state;
      } catch {
        state = 'unreachable';
      }
      if (signal.aborted || !input.mounted.current) return;
      if (state === input.goal) {
        input.setCn(input.ids, { kind: 'confirmed' });
        break;
      }
      if (state === 'not_configured') {
        input.setCn(input.ids, { kind: 'not_configured' });
        break;
      }
      if (Date.now() - startedAt >= pollTiming.deadlineMs) {
        input.setCn(input.ids, { kind: 'timeout', last: state });
        break;
      }
      input.setCn(input.ids, { kind: 'waiting', last: state });
      await sleep(pollTiming.intervalMs, signal);
      if (signal.aborted || !input.mounted.current) return;
    }
    for (const id of input.ids) {
      if (input.pollers.get(id) === controller) input.pollers.delete(id);
    }
  })();
}
